// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./DeviceLeases.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { Button, CircularProgress, IconButton } from "@mui/material";

// MUI ICONS
import EditIcon from "@mui/icons-material/Edit";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import DeviceLeasesEditAddDialog from "./DeviceLeasesEditAddDialog/DeviceLeasesEditAddDialog";
import DeviceLeasesDevices from "./DeviceLeasesDevices/DeviceLeasesDevices";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
	strictSort,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const DeviceLeases = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [leases, setLeases] = useState([]);

	const [leaseRows, setLeaseRows] = useState([]);
	const [leaseColumns, setLeaseColumns] = useState([]);

	const [leasesToDevices, setLeasesToDevices] = useState([]);
	// eventually more device types here
	const [devices, setDevices] = useState([]);
	const [deviceCategories, setDeviceCategories] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [devicesOpen, setDevicesOpen] = useState(false);
	const [recordToShowDevices, setRecordToShowDevices] = useState({});

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Device Leases",
			employeeID: employeeID,
			notes: "Accessed the Device Leases page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (leases.length > 0 && leasesToDevices.length > 0) {
			createRowData();
			createColumnData();
		}
	}, [leases, leasesToDevices]);

	// INPUT HANDLERS

	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	const handleDevicesClick = (record) => {
		setRecordToShowDevices(record);
		setDevicesOpen(true);
	};

	const handleDevicesClose = () => {
		setDevicesOpen(false);
		setRecordToShowDevices({});
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchLeases();
		await fetchDevices();
		await fetchDeviceCategories();
		await fetchLeasesToDevices();
		setLoading(false);
	};

	const fetchLeases = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/leases/all"
			);
			setLeases(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	const fetchDevices = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/all"
			);
			setDevices(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	const fetchDeviceCategories = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/deviceCategories/all"
			);
			setDeviceCategories(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	const fetchLeasesToDevices = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hardwareInventory/devicesToLeases/all"
			);
			setLeasesToDevices(res.data);
			return res.data;
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setLeaseColumns([
			{
				accessorKey: "lease_number",
				header: () => <span>Lease Number</span>,
				label: "Lease Number",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "origin_date",
				header: () => <span>Lease Start</span>,
				label: "Lease Start",
				cell: (props) => (
					<span>
						{convertToPST(props.getValue()) || notApplicable}
					</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "lease_length",
				header: () => <span>Lease Length</span>,
				label: "Lease Length",
				cell: (props) => (
					<span>
						{props.getValue() || notApplicable}{" "}
						{props.getValue() > 1 ? "Months" : "Month"}
					</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "term_date",
				header: () => <span>Lease End</span>,
				label: "Lease End",
				cell: (props) => (
					<span>
						{convertToPST(props.getValue()) || notApplicable}
					</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "devices",
				header: () => <span>Devices</span>,
				label: "Devices",
				cell: (props) => (
					<span
						style={{ cursor: "pointer", color: "#3f51b5" }}
						onClick={() =>
							handleDevicesClick(props.row.original.lease)
						}
					>
						{props.getValue() || notApplicable}
					</span>
				),
				filterType: "textfield",
				filterFn: strictSort,
			},
			{
				accessorKey: "edit",
				header: "",
				label: "Edit",
				cell: (props) => (
					<IconButton
						onClick={() =>
							handleEditClick(props.row.original.lease)
						}
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<EditIcon fontSize="small" />
					</IconButton>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const createRowData = () => {
		setLeaseRows(
			leases.map((lease) => ({
				lease_number: lease.lease_number,
				origin_date: lease.origin_date,
				lease_length: lease.lease_length,
				term_date: lease.term_date,
				devices: leasesToDevices.filter(
					(devices) => devices.lease_id === lease.id
				).length,
				lease: lease, // full lease object for devices and edit
			}))
		);
	};

	// RENDER
	return (
		<div className="DeviceLeases">
			<div className="DeviceLeasesHeader">
				<div className="DeviceLeasesHeaderTitle">
					Device Leases
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Lease
					</Button>
				</div>
			</div>
			{loading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{leases.length === 0 && <J2NoResultsFound />}
					{leases.length > 0 &&
						leaseColumns.length > 0 &&
						leaseRows.length > 0 && (
							<TanStackTable
								columnData={leaseColumns}
								rowData={leaseRows}
							/>
						)}
				</div>
			)}
			<DeviceLeasesEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				recordToEdit={recordToEdit}
				deviceCategories={deviceCategories}
				devices={devices}
				leases={leases}
				editing={editing}
			/>
			<DeviceLeasesDevices
				open={devicesOpen}
				onClose={handleDevicesClose}
				leasesToDevices={leasesToDevices}
				deviceCategories={deviceCategories}
				devices={devices}
				recordToShowDevices={recordToShowDevices}
			/>
		</div>
	);
};

// EXPORT
export default DeviceLeases;
